import { requestData } from "../api-config";

import { SignedPreviewResponse } from "@/types/auth";

import { authService } from "@/services/auth-service";

const SIGNED_URLS_BATCH_SIZE = 100;

export async function getSignedUrl(
  file_path: string,
): Promise<SignedPreviewResponse | null> {
  return await requestData<SignedPreviewResponse>("gcs/document", "get", {
    searchParams: {
      file_path: file_path,
    },
  });
}

export async function getSignedUrlsBatch(
  filePaths: string[],
): Promise<Record<string, string> | null> {
  if (filePaths.length === 0) return {};

  const responses = await Promise.all(
    Array.from(
      { length: Math.ceil(filePaths.length / SIGNED_URLS_BATCH_SIZE) },
      (_, index) =>
        requestData<Record<string, string>>("gcs/documents", "post", {
          json: {
            file_paths: filePaths.slice(
              index * SIGNED_URLS_BATCH_SIZE,
              (index + 1) * SIGNED_URLS_BATCH_SIZE,
            ),
          },
        }),
    ),
  );

  const signedUrls: Record<string, string> = {};
  for (const response of responses) {
    // A null chunk is a transient request failure (after the network layer's
    // own retries). Fail the whole batch rather than returning a partial map:
    // a partial map would make the failed chunk's paths look like permanent
    // per-file omissions, surfacing a false "Preview unavailable" for them.
    if (!response) return null;
    Object.assign(signedUrls, response);
  }

  return signedUrls;
}

export async function getSignedUrlBase64(
  file_path: string,
): Promise<SignedPreviewResponse | null> {
  return await requestData<SignedPreviewResponse>(
    "gcs/document/base64",
    "get",
    {
      searchParams: {
        file_path: file_path,
      },
    },
  );
}

export async function getSignedDownloadUrl(
  file_path: string,
  filename: string,
): Promise<SignedPreviewResponse | null> {
  return await requestData<SignedPreviewResponse>("gcs/document", "get", {
    searchParams: {
      file_path: file_path,
      response_disposition: `attachment; filename="${filename}"`,
    },
  });
}

export async function downloadFromSignedUrl(
  signedUrl: string,
  _filename: string,
): Promise<void> {
  const link = document.createElement("a");
  link.href = signedUrl;
  link.rel = "noopener noreferrer";
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}

// A photo download is abandoned only when no bytes arrive for this long, so
// a big original on slow wifi can take as long as it needs while a stalled
// request still gives up.
const PHOTO_FETCH_IDLE_TIMEOUT_MS = 20_000;

const IMAGE_EXTENSION_BY_TYPE: Record<string, string> = {
  "image/jpeg": "jpg",
  "image/png": "png",
  "image/webp": "webp",
  "image/gif": "gif",
  "image/heic": "heic",
  "image/heif": "heif",
  "image/avif": "avif",
};

// Load a stored photo as a File the page can hand to the share sheet. The
// bytes come through the same-origin image proxy because the bucket does not
// allow the browser to read signed URLs directly (CORS). iOS only offers
// "Save Image" when the file has an image type and extension, so both are set.
export async function fetchAttachmentFile(
  file_path: string,
  filename: string,
  { signal }: { signal?: AbortSignal } = {},
): Promise<File | null> {
  const result = await getSignedUrl(file_path);
  if (!result?.signedUrl || signal?.aborted) return null;

  const controller = new AbortController();
  const abort = () => controller.abort();
  signal?.addEventListener("abort", abort);
  let idleTimer: ReturnType<typeof setTimeout> | undefined;
  const stillReceiving = () => {
    clearTimeout(idleTimer);
    idleTimer = setTimeout(abort, PHOTO_FETCH_IDLE_TIMEOUT_MS);
  };

  try {
    const token = await authService.getToken();
    // The clock starts with the request, so a slow sign-in refresh doesn't
    // count against the photo.
    stillReceiving();
    // size=full: originals can be well over the proxy's default 10 MB cap.
    const res = await fetch(
      `/api/proxy-image?size=full&url=${encodeURIComponent(result.signedUrl)}`,
      {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        signal: controller.signal,
      },
    );
    if (!res.ok || !res.body) return null;

    const chunks: Uint8Array<ArrayBuffer>[] = [];
    const reader = res.body.getReader();
    for (;;) {
      const { done, value } = await reader.read();
      if (done) break;
      chunks.push(value as Uint8Array<ArrayBuffer>);
      stillReceiving();
    }

    // "image/jpeg; charset=…" -> "image/jpeg": iOS only offers "Save Image"
    // for a plain image type.
    const received = (res.headers.get("Content-Type") ?? "")
      .split(";")[0]
      .trim()
      .toLowerCase();
    const type = received.startsWith("image/") ? received : "image/jpeg";
    const extension = IMAGE_EXTENSION_BY_TYPE[type] ?? "jpg";
    // The extension always follows the bytes, so a JPEG named "x.png" is
    // saved as "x.jpg". Only a real image extension is replaced: "Laser 2.1"
    // becomes "Laser 2.1.jpg".
    const base = filename.replace(
      /\.(jpe?g|png|webp|gif|heic|heif|avif)$/i,
      "",
    );
    return new File(chunks, `${base}.${extension}`, { type });
  } finally {
    clearTimeout(idleTimer);
    signal?.removeEventListener("abort", abort);
  }
}

export async function getSignedUrlPublic(
  file_path: string,
): Promise<SignedPreviewResponse | null> {
  return await requestData<SignedPreviewResponse>(
    "gcs/document/public",
    "get",
    {
      searchParams: {
        file_path: file_path,
      },
    },
  );
}
